import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Upload, FileText, Trash2, Loader2, Paperclip } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const isImage = (ct) => (ct || '').startsWith('image/');
const fmtSize = (b) => b > 1e6 ? `${(b / 1e6).toFixed(1)} MB` : `${Math.round(b / 1e3)} KB`;

export const DocumentUploader = ({ entityType, entityId, label = '', uploadedBy = '', onUploaded, buttonText = 'Upload File', dark = false, compact = false }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFiles = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    for (const file of files) {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('entity_type', entityType);
      fd.append('entity_id', entityId || '');
      fd.append('label', label);
      fd.append('uploaded_by', uploadedBy);
      try {
        const r = await fetch(`${API}/api/uploads/document`, { method: 'POST', body: fd });
        const d = await r.json();
        if (!r.ok) throw new Error(d.detail || 'Upload failed');
        toast.success(`${file.name} uploaded`);
        onUploaded?.(d);
      } catch (e) {
        toast.error(`${file.name}: ${e.message}`);
      }
    }
    setUploading(false);
    if (inputRef.current) inputRef.current.value = '';
  };

  return (
    <>
      <input ref={inputRef} type="file" accept="image/*,application/pdf" multiple className="hidden"
        onChange={e => handleFiles([...e.target.files])} data-testid={`doc-upload-input-${entityType}${label ? `-${label}` : ''}`} />
      <button type="button" onClick={() => inputRef.current?.click()} disabled={uploading}
        className={`inline-flex items-center gap-1.5 font-bold rounded-lg transition-colors disabled:opacity-50 ${compact ? 'text-[11px] px-2.5 py-1.5' : 'text-xs px-3.5 py-2'} ${dark ? 'bg-gray-900 border border-gray-700 text-gray-200 hover:border-gray-500' : 'bg-white border border-gray-300 text-gray-700 hover:border-black'}`}
        data-testid={`doc-upload-btn-${entityType}${label ? `-${label}` : ''}`}>
        {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
        {uploading ? 'Uploading…' : buttonText}
      </button>
    </>
  );
};

export const AttachmentList = ({ entityType, entityId, refreshKey = 0, canDelete = true, dark = false, emptyText = 'No files attached yet.' }) => {
  const [docs, setDocs] = useState([]);
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(() => {
    if (!entityId) { setDocs([]); setLoaded(true); return; }
    fetch(`${API}/api/uploads/documents?entity_type=${encodeURIComponent(entityType)}&entity_id=${encodeURIComponent(entityId)}`)
      .then(r => r.ok ? r.json() : { documents: [] })
      .then(d => { setDocs(d.documents || []); setLoaded(true); })
      .catch(() => setLoaded(true));
  }, [entityType, entityId]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const remove = async (fid) => {
    if (!window.confirm('Delete this file?')) return;
    await fetch(`${API}/api/uploads/files/${fid}`, { method: 'DELETE' });
    setDocs(prev => prev.filter(d => d.file_id !== fid));
    toast.success('File deleted');
  };

  if (!loaded) return <p className={`text-xs ${dark ? 'text-gray-500' : 'text-gray-400'}`}>Loading files…</p>;
  if (docs.length === 0) return <p className={`text-xs ${dark ? 'text-gray-500' : 'text-gray-400'}`} data-testid={`attachments-empty-${entityType}`}>{emptyText}</p>;

  return (
    <div className="space-y-1.5" data-testid={`attachments-list-${entityType}`}>
      {docs.map(d => (
        <div key={d.file_id} className={`flex items-center gap-2.5 rounded-lg px-2.5 py-2 border ${dark ? 'bg-gray-900 border-gray-800' : 'bg-gray-50 border-gray-200'}`} data-testid={`attachment-${d.file_id}`}>
          <a href={`${API}${d.url}`} target="_blank" rel="noreferrer" className="shrink-0">
            {isImage(d.content_type) ? (
              <img src={`${API}${d.url}`} alt={d.label || d.original_filename} className="h-10 w-10 rounded object-cover border border-gray-300" loading="lazy" />
            ) : (
              <span className={`h-10 w-10 rounded flex items-center justify-center ${dark ? 'bg-gray-800' : 'bg-red-50'}`}><FileText className="h-4 w-4 text-red-500" /></span>
            )}
          </a>
          <a href={`${API}${d.url}`} target="_blank" rel="noreferrer" className="min-w-0 flex-1">
            <p className={`text-xs font-semibold truncate ${dark ? 'text-gray-200' : 'text-gray-900'}`}>{d.label ? `${d.label} — ` : ''}{d.original_filename}</p>
            <p className={`text-[10px] ${dark ? 'text-gray-500' : 'text-gray-400'}`}>{fmtSize(d.size || 0)} · {(d.created_at || '').slice(0, 10)}{d.uploaded_by ? ` · ${d.uploaded_by}` : ''}</p>
          </a>
          {canDelete && (
            <button type="button" onClick={() => remove(d.file_id)} className="shrink-0 text-gray-400 hover:text-red-600 p-1" data-testid={`attachment-delete-${d.file_id}`}>
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      ))}
    </div>
  );
};

export const AttachmentsSection = ({ entityType, entityId, title = 'Files & Attachments', uploadedBy = '', dark = false }) => {
  const [refreshKey, setRefreshKey] = useState(0);
  return (
    <div data-testid={`attachments-section-${entityType}`}>
      <div className="flex items-center justify-between mb-2">
        <p className={`text-[10px] font-bold uppercase tracking-wider flex items-center gap-1.5 ${dark ? 'text-gray-400' : 'text-gray-500'}`}>
          <Paperclip className="h-3 w-3" /> {title}
        </p>
        <DocumentUploader entityType={entityType} entityId={entityId} uploadedBy={uploadedBy} compact dark={dark}
          buttonText="Add File" onUploaded={() => setRefreshKey(k => k + 1)} />
      </div>
      <AttachmentList entityType={entityType} entityId={entityId} refreshKey={refreshKey} dark={dark} />
    </div>
  );
};
